import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

import { SingleOrArray } from '@plugins/shared/types/helpers';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';
import { isNullish } from '@plugins/shared/utils/assertFunctions';
import { deduplicateAndFilterStringParams } from '@plugins/shared/utils/filterUtils/deduplicate-and-filter';
import type { IdentityPublic } from '@plugins/userdetails-lib/entity';

import { TRANSLATION_KEYS } from '../../private/config';
import type {
  EntityCacheCheckReturn,
  MissingEntity,
  SearchSwitch,
  SearchTypes,
  SearchOptions,
  AllEntityTypes,
  RequestStatus,
  ApiEntityTypes,
  ValidSearchTypes,
  FindEntityReturn,
  ApiSearchRequestDto,
  FindValidEntityReturn,
  ValidEntitiesReturn,
} from '../../private/utils/types.private';

import { EntitiesCacheManagementService } from './entities-cache-management.service';
import { EntitiesRequestsService } from './entities-requests.service';

const missingEntityKey = TRANSLATION_KEYS.ATOMIC.missing;

/** Main service through which components and directives communicate,
 * receive data from and send updates to. The `UserDetailsService`, in turn,
 * communicates with the `EntitiesCacheManagementService` and `RequestsService`.
 *
 * The service's method are usually triggered internally by the module's components and directives.
 *
 * **Responsibilities:**
 * - Storing and dispatching placeholders for deleted entities.
 * - Managing the finding / searching for entities
 * and cache pre-population together with auxiliary services.
 * - Managing pending status (`pending|success|error`) of requested entities
 *  called via the `modifyPendingStatus` method.
 *
 * The service itself does not directly manage or update caches or create and send requests.
 * This is done by invoking the appropriate methods from the `CacheManagementService`, `RequestService`. */
@Injectable()
export class UserDetailsService {
  entityTypes: (keyof SearchSwitch)[] = ['user', 'group'];
  locale!: string;

  private pendingIdRequests = new BehaviorSubject(
    new Map<string, RequestStatus>(),
  );
  pendingIdRequests$ = this.pendingIdRequests.asObservable();
  getPendingIdRequest(id: string): RequestStatus | undefined {
    return this.pendingIdRequests.getValue().get(id);
  }

  private cacheService = inject(EntitiesCacheManagementService);
  private requestService = inject(EntitiesRequestsService);

  /** Returns typed entity responses that correspond to the supplied search type
   * If requests complete successfullyy, but id's do not resolve to an entity,
   * a `MissingEntity` type will be returned for those ids instead.
   *
   *
   * Any leftover ids will be typed as `FailedEntitiy` types. Similarly, if the method
   * throws, all ids will be types as failed. Failed entities will be re-fetched again
   * on the next try. */
  async findEntity<T extends ValidSearchTypes>(
    id: SingleOrArray<string>,
    type: T = 'all' as T,
  ): Promise<FindEntityReturn<T>> {
    const ids = returnAsArray(id);
    try {
      const cleanedIds = deduplicateAndFilterStringParams(ids);
      const entities = await this.getEntityDetails(cleanedIds, type);
      const entityIds = entities.map(({ id }) => id);
      return [
        ...entities,
        ...cleanedIds
          .filter((id) => !entityIds.includes(id))
          .map((id) => ({ id, type: 'error' })),
      ] as FindEntityReturn<T>;
    } catch {
      return ids.map((id) => ({ id, type: 'error' }));
    }
  }

  /** Returns only valid entities (users, groups, or missing entities)
   * with failed entities filtered out. */
  async findValidEntities<T extends ValidSearchTypes>(
    id: SingleOrArray<string>,
    type: T = 'all' as T,
  ): Promise<FindValidEntityReturn<T>> {
    const results = await this.findEntity(id, type);
    return results.filter(
      (arg): arg is ValidEntitiesReturn<T> => arg.type !== 'error',
    );
  }

  /** Returns all entities, whose names partially match the provided term (not case sensitive).
   * Can filter out entries passed in pickedEntities and excludedEntities */
  async searchEntities(
    term: string,
    options: SearchOptions = {
      pickedEntities: [],
      excludedEntities: [],
      type: 'all',
      includedGroups: [],
    },
  ): Promise<IdentityPublic[]> {
    const {
      pickedEntities,
      excludedEntities,
      type,
      includedGroups: initalIncludedGroups,
    } = options;
    if (isNullish(type) || type === 'none') {
      return [];
    }
    const searchCache = this.cacheService.searchCache;
    const includedGroups = type !== 'group' ? initalIncludedGroups : [];

    // If cache exists, check for term and type matches and return if found.
    if (searchCache.size) {
      const termCacheCheck = await this.cacheService.checkSearchCache(
        { search: term, includedGroups },
        type,
      );
      if (termCacheCheck.length) {
        const retrievedEntities = await this.getEntityDetails(termCacheCheck);
        return this.cleanSearchReturn({
          retrievedEntities,
          pickedEntities,
          excludedEntities,
        });
      }
    }
    // If no match from cache, request data, update cache and return result.
    const retrievedEntities = await this.searchRequest(
      { search: term, includedGroups },
      type,
    );
    if (!retrievedEntities.length) return [];
    // Clean entity list of picked users for return, but populate cache
    // with entire response.
    const cleanedEntities = this.cleanSearchReturn({
      retrievedEntities,
      pickedEntities,
      excludedEntities,
    });
    await this.cacheService.updateSearchCache(
      { search: term, includedGroups },
      retrievedEntities,
      type,
    );
    return cleanedEntities;
  }

  /** Pre populate entity cache to avoid making API calls. */
  populateCache(entities: IdentityPublic[]): void {
    this.cacheService.prePopulateCache(entities);
  }

  async isUserAuthorized(
    userId: string,
    authorizedList: string[],
  ): Promise<boolean> {
    if (!authorizedList.length) return true;
    if (authorizedList.some((id) => userId === id)) return true;

    // Group check. Get user's data first.
    // Return false, if fetch is unsuccessful or if user is missing.
    const userData = (await this.findEntity(userId, 'user'))[0];
    if (userData.type === 'error' || userData.type === 'missing') return false;

    const groups = userData.groups;
    if (!(groups && groups.length)) return false;
    if (authorizedList.some((id) => groups.includes(id))) return true;
    return false;
  }

  private async getEntityDetails(
    ids: string[],
    type: SearchTypes = 'all',
  ): Promise<AllEntityTypes[]> {
    if (type === 'none') return [];
    if (!ids.length) return [];

    // Ensure up-to-date cache information in cacheCheck, newlyMissing, and newEntities
    // via observables.
    let cacheCheck: EntityCacheCheckReturn =
      this.cacheService.checkEntityCache(ids);

    const notCachedIds = cacheCheck.notCached;
    // If all ids are cached, prune requests, cleanup subscriptions, and return values.
    if (!notCachedIds.length) {
      // Since Cache services are provided in root, there is a possibility that entity's may already
      // be cached, but their pending statuses are not properly taken care of.
      this.modifyPendingStatus(ids, 'success');
      return [
        ...cacheCheck.existing,
        ...(await this.createMissingPlaceholderEntity(cacheCheck.missing)),
      ];
    }
    await this.findRequest(notCachedIds, type);

    cacheCheck = this.cacheService.checkEntityCache(ids);

    const allMissingEntities = await this.createMissingPlaceholderEntity([
      ...cacheCheck.missing,
    ]);
    return [...cacheCheck.existing, ...allMissingEntities];
  }

  private cleanSearchReturn({
    retrievedEntities,
    pickedEntities = [],
    excludedEntities = [],
  }: {
    retrievedEntities: AllEntityTypes[];
    pickedEntities?: string[];
    excludedEntities?: string[];
  }): IdentityPublic[] {
    return retrievedEntities
      .filter((entity): entity is IdentityPublic => entity.type !== 'missing')
      .filter((entity) => !pickedEntities.includes(entity.id))
      .filter((entity) => !excludedEntities.includes(entity.id));
  }

  private async createMissingPlaceholderEntity(
    ids: string[],
  ): Promise<MissingEntity[]> {
    const missingEntities: MissingEntity[] = ids.map((missingId) => ({
      id: missingId,
      name: missingEntityKey,
      type: 'missing',
    }));
    return missingEntities;
  }

  /**Set status of request ids to the pendingIdRequests Behavior subject. */
  private modifyPendingStatus(
    ids: string | string[],
    status: RequestStatus,
  ): void {
    const targetIds = returnAsArray(ids);
    const requestMap = this.pendingIdRequests.getValue();
    targetIds
      // Filter out any invalid ids, such as undefined or null values
      .filter(Boolean)
      .forEach((id) => {
        // Only update non-successful and non-existing ids.
        if (!requestMap.has(id) || requestMap.get(id) !== 'success') {
          requestMap.set(id, status);
        }
      });
    this.pendingIdRequests.next(requestMap);
  }

  protected clearPendingMap(): void {
    this.pendingIdRequests.next(new Map<string, RequestStatus>());
  }

  /** Search helper method which also handles request status */
  private async searchRequest(
    query: ApiSearchRequestDto,
    groupType: ApiEntityTypes,
  ): Promise<IdentityPublic[]> {
    try {
      const entities = await lastValueFrom(
        this.requestService.directRequest('search', query, groupType),
      );
      this.modifyPendingStatus(
        entities.map(({ id }) => id),
        'success',
      );
      return entities;
    } catch {
      return [];
    }
  }

  /** Find helper method which also handles request status */
  private async findRequest(
    ids: string | string[],
    groupType: ApiEntityTypes = 'all',
  ): Promise<void> {
    const targetIds = returnAsArray(ids);
    this.modifyPendingStatus(targetIds, 'pending');
    try {
      await this.requestService.handleRequest(
        'details',
        { id: targetIds },
        groupType,
      );
      this.modifyPendingStatus(targetIds, 'success');
    } catch {
      this.modifyPendingStatus(targetIds, 'error');
    }
  }
}
